import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getOS } from "@service/serviceOrder";
import { AxiosError } from "axios";
import {
  popupError,
  popupSuccess,
  popupWarning,
  getCurrentTicketId,
  getCurrentTicketType,
} from "@service/zendesk";
import { type ServiceOrder } from "@interfaces";
import { createTicket } from "@service/ticket";
import { isValidCPF } from "@utils/validateCPF";
import { showMacroContent } from "@service/zendesk";

export const useSearchOS = () => {
  const navigate = useNavigate();
  const [osSearched, setOsSearched] = useState<ServiceOrder | ServiceOrder[] | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [showInteractions, setShowInteractions] = useState<boolean>(false);
  const [osSaved, setOsSaved] = useState<ServiceOrder>();
  const [confirm, setConfirm] = useState<boolean>(false);
  const [requestValue, setRequestValue] = useState<"cpf" | "os" | "serie">("os");
  const [mainTicket, setMainTicket] = useState<string>("");
  const [macro, setMacro] = useState<string>();

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;

  useEffect(() => {
    (async () => {
      setLoading(true);
      const currentTicket = await getCurrentTicketId();
      setMainTicket(currentTicket);
      const response = await showMacroContent();
      const macroContent = response.actions[0].value;

      const plainText =
        new DOMParser().parseFromString(macroContent ?? "", "text/html").body.textContent || "";

      setMacro(plainText);
      setLoading(false);
    })();
  }, []);

  const handleSearch = async () => {
    if (inputValue === "") return;

    const cleanedInput = inputValue.replace(/\D/g, "");
    const hasLetters = /[a-zA-Z]/.test(inputValue);

    let searchType: "cpf" | "os" | "serie" = "os";
    let searchValue = inputValue;

    if (cleanedInput.length === 11 && !hasLetters) {
      const isCpf = isValidCPF(cleanedInput);
      if (isCpf) {
        searchType = "cpf";
        searchValue = cleanedInput;
        setInputValue(cleanedInput);
      } else {
        popupError("CPF inválido. Verifique os dados informados.", false);
        return;
      }
    } else if (hasLetters) {
      searchType = "serie";
    } else {
      searchType = "os";
    }

    setRequestValue(searchType);
    setLoading(true);

    const fetchWithRetry = async (attempt: number = 1): Promise<boolean> => {
      try {
        const response = await getOS(searchValue, searchType);

        if (response.status === 200) {
          if (response.data) {
            popupSuccess("OS encontrada com sucesso.", false);
            setOsSearched(
              searchType === "cpf" || searchType === "serie"
                ? response.data.response
                : response.data
            );
            return true;
          } else {
            const searchTypeText =
              searchType === "cpf" ? "CPF" : searchType === "serie" ? "número de série" : "OS";
            popupError(`Nenhuma OS encontrada para esse ${searchTypeText}`, false);
            setOsSearched(undefined);
            return false;
          }
        }
        return false;
      } catch (err) {
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          return fetchWithRetry(attempt + 1);
        }
        throw err;
      }
    };

    try {
      await fetchWithRetry();
    } catch (err) {
      setOsSearched(undefined);

      const error = err as AxiosError;
      if (error.response?.status === 404) {
        popupError("Nenhuma OS encontrada", false);
      } else {
        popupError("Erro inesperado. Tente novamente mais tarde.", false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const backPage = (pagesToback: number) => {
    navigate(pagesToback);
  };

  const handleShowInteractions = () => {
    setShowInteractions(true);
  };

  const backToSearchPage = () => {
    setShowInteractions(false);
    setConfirm(false);
  };

  const saveOs = (os: ServiceOrder) => {
    setOsSaved(os);
  };

  const goToConfirm = async () => {
    const ticketType = await getCurrentTicketType();

    if (ticketType === "incident") {
      popupWarning("Verifique se o ticket aberto é do tipo 'Problema'.", true);
      return;
    }

    setConfirm(true);
  };

  const handleCreateTicket = async (os: ServiceOrder, description: string, mainTicket: number) => {
    if (!os) return;
    setLoading(true);

    try {
      const response = await createTicket(os, description, mainTicket);

      if (response) {
        navigate(`/success/Ticket/${response.ticket.id}`);
      }
    } catch (err) {
      const error = err as any;
      console.error("Erro ao criar ticket:", err);
      const responseData = error?.responseJSON ?? error?.response?.data;
      const errTicket =
        responseData?.details?.base?.[0]?.description ??
        responseData?.details?.["custom_field_values.base"]?.[0]?.description;
      if (errTicket) {
        popupError(`Erro ao criar ticket: ${errTicket}`, false);
      } else {
        const details = responseData?.details;
        const firstKey = details ? Object.keys(details)[0] : null;
        const message = firstKey ? details[firstKey]?.[0]?.description : null;
        const nativeMessage = err instanceof Error ? err.message : null;

        popupError(
          message || nativeMessage || "Erro ao criar ticket. Tente novamente mais tarde",
          true
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    backPage,
    handleInputChange,
    osSearched,
    loading,
    handleShowInteractions,
    backToSearchPage,
    showInteractions,
    handleCreateTicket,
    requestValue,
    saveOs,
    osSaved,
    confirm,
    goToConfirm,
    mainTicket,
    macro,
    handleSearch,
  };
};
